// Compact sign-in overlay for the shell header: an email magic-link (and
// Google, when the server has it configured). The full /auth page keeps the
// password + sign-up modes; this is the quick door from anywhere in the app.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/ui/Modal";
import { Field } from "@/ui/Field";
import { Button } from "@/ui/Button";
import { useAuth } from "@/state/auth";
import { anonymousId } from "@/analytics/service";
import { api } from "@/lib/api";
import "@/views/auth/auth.css";

export function SignInModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { magic } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<"email" | "dev-inbox" | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [googleOn, setGoogleOn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    // Fresh state each open; only reveal Google when the server has it wired.
    setSent(null);
    setDevLink(null);
    setError(null);
    setBusy(false);
    setEmail("");
    api<{ google?: boolean }>("/api/health")
      .then((r) => setGoogleOn(Boolean(r.google)))
      .catch(() => undefined);
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const delivered = await magic(email.trim());
      setSent(delivered);
      // No mail provider (local/dev): surface the link from the dev inbox so
      // sign-in can be completed without an actual email.
      if (delivered === "dev-inbox") {
        const res = await api<{ inbox: Array<{ email: string; link: string }> }>("/api/auth/dev-inbox").catch(() => null);
        setDevLink(res?.inbox?.[0]?.link ?? null);
      }
    } catch {
      setError("Couldn't send the link just now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Sign in to Beat Draft">
      {sent ? (
        <div className="bd-auth__sent" role="status">
          {sent === "email" ? (
            <p>The link is on its way to {email}. It works once and rests in 15 minutes.</p>
          ) : (
            <>
              <p className="bd-auth__dev-note">No mail service is configured here, so your link landed in the dev inbox:</p>
              {devLink && (
                <a className="bd-auth__dev-link" href={devLink}>
                  Open your door →
                </a>
              )}
            </>
          )}
        </div>
      ) : (
        <form className="bd-auth__form" onSubmit={submit}>
          <Field
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="you@email.com"
            error={error ?? undefined}
          />
          <Button variant="primary" size="lg" type="submit" loading={busy} loadingLabel="Sending the link">
            Email me a sign-in link
          </Button>

          {googleOn && (
            <>
              <div className="bd-auth__or">
                <span>or</span>
              </div>
              <a className="bd-auth__oauth" href={`/api/auth/google?anon=${encodeURIComponent(anonymousId())}`}>
                <GoogleMark />
                Continue with Google
              </a>
            </>
          )}

          <div className="bd-auth__switch">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/auth");
              }}
            >
              Use a password instead
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// Google's brand mark. Fixed brand colors by design (a logo, not a UI element).
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}
